import { OvCard, OvSelect, PageWrapper } from '@components'
import { LABELS } from '@constants'
import { formatValueToCurrency } from '@helpers/formatCentToAmount.helper'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import {
  Assessment,
  Assignment,
  CheckCircle,
  Description,
  Euro,
  SvgIconComponent,
  TrendingUp,
  Warning,
} from '@mui/icons-material'
import {
  alpha,
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

interface MonthlyRevenue {
  month: number
  revenue: number
}

interface TopClient {
  client_id: number
  client_name: string
  invoice_count: number
  total_revenue: number
}

interface RecentDocument {
  id: number
  type: string
  type_label: string
  number: string
  client_name: string
  finalised_at: string
  amount: number
}

interface OverdueInvoice {
  id: number
  document_number: string
  client_name: string
  amount: number
  expires_at: string
  days_overdue: number
}

const KpiCard = observer(
  ({
    title,
    value,
    subtitle,
    icon: Icon,
    color = 'primary',
  }: {
    title: string
    value: string | number
    subtitle?: string
    icon?: SvgIconComponent
    color?: 'primary' | 'success' | 'warning' | 'error' | 'info'
  }) => (
    <Box
      sx={{
        height: '100%',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: theme => `0 4px 12px ${alpha(theme.palette[color].main, 0.15)}`,
        },
      }}>
      <OvCard>
        <Stack
          spacing={1}
          height="100%">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start">
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5, fontWeight: 500 }}>
                {title}
              </Typography>
              <Typography
                variant="h3"
                color={`${color}.main`}
                sx={{ fontWeight: 700 }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.25, display: 'block' }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {Icon && (
              <Box
                sx={{
                  bgcolor: theme => alpha(theme.palette[color].main, 0.1),
                  color: `${color}.main`,
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                }}>
                <Icon sx={{ fontSize: 28 }} />
              </Box>
            )}
          </Stack>
        </Stack>
      </OvCard>
    </Box>
  )
)

const SectionHeader = ({ title, icon: Icon }: { title: string; icon?: SvgIconComponent }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{ mb: 1.5 }}>
    {Icon && (
      <Box
        sx={{
          color: 'primary.main',
          display: 'flex',
          alignItems: 'center',
        }}>
        <Icon />
      </Box>
    )}
    <Typography
      variant="h6"
      sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
  </Stack>
)

const MonthlyRevenueBar = observer(
  ({
    month,
    revenue,
    maxRevenue,
    isMobile,
  }: {
    month: number
    revenue: number
    maxRevenue: number
    isMobile: boolean
  }) => {
    const monthNames = [
      'Jan',
      'Feb',
      'Mrt',
      'Apr',
      'Mei',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dec',
    ]
    const percentage = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0
    const heightPercentage = Math.max(percentage, 2) // Minimum 2% for visibility

    return (
      <Stack
        spacing={0.5}
        alignItems="center"
        sx={{ flex: 1, minWidth: isMobile ? '20px' : '40px' }}>
        {!isMobile && (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              fontSize: '0.65rem',
              minHeight: '16px',
            }}>
            {revenue > 0 ? formatValueToCurrency(revenue) : ''}
          </Typography>
        )}
        <Tooltip
          title={formatValueToCurrency(revenue)}
          arrow
          placement="top">
          <Box
            sx={{
              width: '100%',
              maxWidth: isMobile ? '14px' : '24px',
              height: isMobile ? 100 : 140,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              position: 'relative',
              cursor: 'pointer',
            }}>
            <Box
              sx={{
                height: `${heightPercentage}%`,
                bgcolor: 'primary.main',
                borderRadius: '6px 6px 0 0',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: theme => `0 -2px 6px ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: theme => `0 -3px 10px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transform: 'scaleY(1.01)',
                  transformOrigin: 'bottom',
                },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '40%',
                  background: theme =>
                    `linear-gradient(to bottom, ${alpha(
                      theme.palette.common.white,
                      0.2
                    )}, transparent)`,
                  borderRadius: '6px 6px 0 0',
                  pointerEvents: 'none',
                },
              }}
            />
          </Box>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: 'text.secondary',
            fontSize: isMobile ? '0.55rem' : '0.7rem',
          }}>
          {monthNames[month - 1]}
        </Typography>
      </Stack>
    )
  }
)

export const DashboardView = observer(() => {
  const { user, company, dashboard } = useStore()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const yearOptions = useMemo(() => dashboard.createYearOptions(), [])
  const quarterOptions = useMemo(() => dashboard.createQuarterOptions(), [])

  useEffect(() => {
    dashboard.getData()
  }, [])

  if (!dashboard.data) {
    return (
      <PageWrapper pageTitle={LABELS.DASHOARD}>
        <Typography>Gegevens laden...</Typography>
      </PageWrapper>
    )
  }

  const { data } = dashboard
  const maxRevenue = Math.max(...data.monthly_revenue.map((d: MonthlyRevenue) => d.revenue || 0))

  return (
    <PageWrapper pageTitle={LABELS.DASHOARD}>
      <Stack spacing={3}>
        {/* Header Section with Filters */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}>
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, mb: 0.5 }}>
              Welkom terug, {user.current_user_name}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary">
              {company?.company?.name}
            </Typography>
          </Box>

          {/* Filters */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              py: { xs: 0, md: 1 },
              px: { xs: 0, md: 2 },
              bgcolor: 'grey.50',
              borderRadius: 1,
              width: { xs: '100%', md: 'auto' },
            }}>
            <Box sx={{ flex: { xs: 1, md: 0 }, minWidth: { xs: 0, md: 120 } }}>
              <OvSelect
                label={LABELS.TAX_YEAR}
                options={yearOptions}
                valueKey="value"
                labelKey="label"
                value={dashboard.selectedYear}
                output="value"
                onChange={e => dashboard.handleSelectedYear(e.target.value as number)}
              />
            </Box>
            <Box sx={{ flex: { xs: 1, md: 0 }, minWidth: { xs: 0, md: 140 } }}>
              <OvSelect
                label="Kwartaal"
                options={quarterOptions}
                valueKey="value"
                labelKey="label"
                value={dashboard.selectedQuarter === null ? 'full' : dashboard.selectedQuarter}
                output="value"
                onChange={e =>
                  dashboard.handleSelectedQuarter(
                    e.target.value === 'full' || e.target.value === 'null' || e.target.value === null
                      ? null
                      : (Number(e.target.value) as 1 | 2 | 3 | 4)
                  )
                }
              />
            </Box>
          </Stack>
        </Stack>

        {/* Monthly Revenue Chart & Top 5 Klanten Side by Side */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
            },
            gap: 3,
          }}>
          {/* Monthly Revenue Chart */}
          <OvCard>
            <SectionHeader title="Maandelijkse Omzet" />
            <Box
              sx={{
                position: 'relative',
                pt: 2,
                pb: 1,
                px: isMobile ? 0.5 : 1,
                bgcolor: theme => alpha(theme.palette.grey[50], 0.3),
                borderRadius: 1,
              }}>
              {/* Grid lines */}
              {!isMobile && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 32,
                    left: 8,
                    right: 8,
                    height: 140,
                    pointerEvents: 'none',
                  }}>
                  {[0, 33, 66, 100].map((percent) => (
                    <Box
                      key={percent}
                      sx={{
                        position: 'absolute',
                        top: `${percent}%`,
                        left: 0,
                        right: 0,
                        height: '1px',
                        bgcolor: 'divider',
                        opacity: 0.3,
                      }}
                    />
                  ))}
                </Box>
              )}
              <Stack
                direction="row"
                spacing={isMobile ? 0.25 : 0.5}
                sx={{
                  justifyContent: 'space-evenly',
                  position: 'relative',
                }}>
                {data.monthly_revenue.map((month: MonthlyRevenue) => (
                  <MonthlyRevenueBar
                    key={month.month}
                    month={month.month}
                    revenue={month.revenue}
                    maxRevenue={maxRevenue}
                    isMobile={isMobile}
                  />
                ))}
              </Stack>
            </Box>
          </OvCard>

          {/* Top 5 Klanten */}
          <OvCard>
            <SectionHeader title="Top 5 Klanten" />
            {data.top_clients.length > 0 ? (
              <List disablePadding>
                {data.top_clients.map((client: TopClient, index: number) => (
                  <Box key={client.client_id}>
                    <ListItemButton
                      onClick={() => navigate(`/klanten/${client.client_id}`, { state: { from: 'dashboard' } })}
                      sx={{ py: 1.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          mr: 2,
                        }}>
                        {index + 1}
                      </Box>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500 }}>
                            {client.client_name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="text.secondary">
                            {client.invoice_count} facturen
                          </Typography>
                        }
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        color="primary">
                        {formatValueToCurrency(client.total_revenue)}
                      </Typography>
                    </ListItemButton>
                    {index < data.top_clients.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ py: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary">
                  Geen klantgegevens beschikbaar
                </Typography>
              </Stack>
            )}
          </OvCard>
        </Box>

        {/* Key Metrics Section */}
        <Box>
          <SectionHeader
            title="Belangrijkste Cijfers"
            icon={Assessment}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                },
                gap: 3,
              }}>
              <KpiCard
                title="Totale Omzet (ex BTW)"
                value={formatValueToCurrency(data.revenue.total_revenue_excl_vat)}
                icon={Euro}
                color="primary"
              />
              <KpiCard
                title="Gerekende BTW"
                value={formatValueToCurrency(data.revenue.total_vat)}
                icon={TrendingUp}
                color="success"
              />
            </Box>
          </Box>
        </Box>

        {/* Status Overview Section */}
        <Box>
          <SectionHeader
            title="Status Overzicht"
            icon={Assignment}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}>
            {/* Invoice Status */}
            <OvCard>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2 }}>
                Facturen Status
              </Typography>
              <Stack spacing={1.5}>
                <Box
                  onClick={() => navigate('/facturen?payment_status=unpaid,sent')}
                  sx={{
                    cursor: 'pointer',
                    p: 1.5,
                    mx: -2,
                    borderRadius: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: theme => alpha(theme.palette.info.main, 0.05),
                    },
                  }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.5 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center">
                      <CheckCircle sx={{ fontSize: 20, color: 'info.main' }} />
                      <Typography
                        variant="body2"
                        color="text.secondary">
                        Openstaand
                      </Typography>
                    </Stack>
                    <Chip
                      label={data.invoice_status.outstanding_count}
                      size="small"
                      color="info"
                    />
                  </Stack>
                  <Typography
                    variant="h6"
                    color="info.main"
                    sx={{ fontWeight: 600 }}>
                    {formatValueToCurrency(data.invoice_status.outstanding_value)}
                  </Typography>
                </Box>

                <Divider />

                <Box
                  onClick={() => navigate('/facturen?payment_status=overdue')}
                  sx={{
                    cursor: 'pointer',
                    p: 1.5,
                    mx: -2,
                    borderRadius: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: theme => alpha(theme.palette.warning.main, 0.05),
                    },
                  }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.5 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center">
                      <Warning sx={{ fontSize: 20, color: 'warning.main' }} />
                      <Typography
                        variant="body2"
                        color="text.secondary">
                        Verlopen
                      </Typography>
                    </Stack>
                    <Chip
                      label={data.invoice_status.overdue_count}
                      size="small"
                      color="warning"
                    />
                  </Stack>
                  <Typography
                    variant="h6"
                    color="warning.main"
                    sx={{ fontWeight: 600 }}>
                    {formatValueToCurrency(data.invoice_status.overdue_value)}
                  </Typography>
                </Box>

                <Divider />

                <Box
                  sx={{
                    p: 1.5,
                    mx: -2,
                  }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.5 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center">
                      <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                      <Typography
                        variant="body2"
                        color="text.secondary">
                        Betaald
                      </Typography>
                    </Stack>
                    <Chip
                      label={data.invoice_status.paid_count}
                      size="small"
                      color="success"
                    />
                  </Stack>
                  <Typography
                    variant="h6"
                    color="success.main"
                    sx={{ fontWeight: 600 }}>
                    {formatValueToCurrency(data.invoice_status.paid_value)}
                  </Typography>
                </Box>
              </Stack>
            </OvCard>

            {/* Quotations */}
            <OvCard>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2 }}>
                Offertes
              </Typography>
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography
                    variant="body2"
                    color="text.secondary">
                    Totaal
                  </Typography>
                  <Chip
                    label={data.quotations.total_quotations}
                    color="primary"
                    size="small"
                  />
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography
                    variant="body2"
                    color="text.secondary">
                    Definitief
                  </Typography>
                  <Chip
                    label={data.quotations.finalised_quotations}
                    color="success"
                    size="small"
                  />
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography
                    variant="body2"
                    color="text.secondary">
                    Concept
                  </Typography>
                  <Chip
                    label={data.quotations.draft_quotations}
                    color="default"
                    size="small"
                  />
                </Stack>
                <Divider />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}>
                    Totale Waarde
                  </Typography>
                  <Typography
                    variant="h6"
                    color="primary"
                    sx={{ fontWeight: 600 }}>
                    {formatValueToCurrency(data.quotations.quotation_value)}
                  </Typography>
                </Stack>
              </Stack>
            </OvCard>

            {/* Verlopen Facturen */}
            <OvCard>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 2 }}>
                Verlopen Facturen
              </Typography>
              {data.overdue_invoices.length > 0 ? (
                <List disablePadding>
                  {data.overdue_invoices.map((invoice: OverdueInvoice, index: number) => (
                    <Box key={invoice.id}>
                      <ListItemButton
                        onClick={() => navigate(`/facturen/${invoice.id}`, { state: { from: 'dashboard' } })}
                        sx={{ py: 1.5 }}>
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}>
                                {invoice.document_number}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ fontWeight: 600 }}>
                                {formatValueToCurrency(invoice.amount)}
                              </Typography>
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={0.25}>
                              <Typography
                                variant="caption"
                                color="text.secondary">
                                {invoice.client_name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="warning.main"
                                sx={{ fontWeight: 500 }}>
                                {invoice.days_overdue} {invoice.days_overdue === 1 ? 'dag' : 'dagen'} te laat
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItemButton>
                      {index < data.overdue_invoices.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ py: 3 }}>
                  <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 0.5 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center">
                    Geen verlopen facturen
                  </Typography>
                </Stack>
              )}
            </OvCard>
          </Box>
        </Box>

        {/* Recent Activity */}
        <OvCard>
          <SectionHeader title="Recente Activiteit" />
          {data.recent_documents.length > 0 ? (
            <List disablePadding>
              {data.recent_documents.map((doc: RecentDocument, index: number) => (
                <Box key={doc.id}>
                  <ListItemButton
                    onClick={() =>
                      navigate(
                        doc.type === 'invoice' ? `/facturen/${doc.id}` : `/offertes/${doc.id}`,
                        { state: { from: 'dashboard' } }
                      )
                    }
                    sx={{ py: 1.5 }}>
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center">
                          <Chip
                            label={doc.type_label}
                            size="small"
                            color={doc.type === 'invoice' ? 'primary' : 'default'}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500 }}>
                            {doc.number}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 0.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary">
                            {doc.client_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary">
                            • {dayjs(doc.finalised_at).format('DD MMM YYYY')}
                          </Typography>
                        </Stack>
                      }
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}>
                      {formatValueToCurrency(doc.amount)}
                    </Typography>
                  </ListItemButton>
                  {index < data.recent_documents.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 3 }}>
              <Typography
                variant="body2"
                color="text.secondary">
                Geen recente documenten
              </Typography>
            </Stack>
          )}
        </OvCard>
      </Stack>
    </PageWrapper>
  )
})
